import { Injectable } from '@angular/core';
import { CanvasState } from "./canvass.state";
import * as PIXI from "pixi.js";
import {CanvasTexture, Vector2} from "three";
import {worldActions} from "../../../state/world/world.actions";
import * as uuid from "uuid";
import {ThreeService} from "../../rendering/threejs/three.service";
import {Store} from "@ngrx/store";

export enum CanvasType {
  PostsCanvas = "PostsCanvas"
}

@Injectable({
  providedIn: 'root'
})
export class CanvasService {
  document: any;
  __debug = true;


  constructor(
      private store: Store,
  ) { }


  async setupCanvas(width: number, height: number, canvasType?: CanvasType) {
    if (!canvasType) canvasType = CanvasType.PostsCanvas;
    const canvas = this.__createCanvas(width, height, canvasType);
    const renderer = await this.__createRenderer(
        new Vector2(width, height),
        canvasType
    );
    while (true) {
      await this.__timeout(100);
      if (renderer) break;
    }
    const rendererId = uuid.v4();
    const canvasTexture = new CanvasTexture(
        canvas,
        // Three.UVMapping,
        // Three.ClampToEdgeWrapping,
        // Three.ClampToEdgeWrapping,
        // Three.NearestFilter,
        // Three.NearestFilter,
        // Three.RGBAFormat
    );
    const stage = new PIXI.Container();
    stage.sortableChildren = true
    if (canvasType === CanvasType.PostsCanvas) {
      globalThis.__PIXI_STAGE__ = stage;
      globalThis.__PIXI_RENDERER__ = renderer;
    }
    CanvasState.canvasStages.set(canvasType, stage);
    CanvasState.canvasTextures.set(canvasType, canvasTexture);
    CanvasState.canvasDimensions.set(canvasType, new Vector2(width, height));
    CanvasState.renderers.set(canvasType, renderer);
    await this.loadPixiAssets()
    this.store.dispatch(worldActions.pixiRendererReady({ viewId: "main" }));
  }

  setDocument(document) {
    this.document = document;
  }

  __createCanvas(width: number, height: number, canvasType?: CanvasType) {
    const canvas = this.document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    if (!canvasType) {
      CanvasState.canvass.set(CanvasType.PostsCanvas, canvas);
    } else {
      CanvasState.canvass.set(canvasType, canvas);
    }
    return canvas;
  }


  __timeout(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }


  async __createRenderer(dimensions: Vector2, canvasType?: CanvasType, ): Promise<PIXI.Renderer> {
    const canvas = this.getCanvasFromType(canvasType);
    const opts = {
      resolution: 1, // Increases size of underlying canvas. No need to scale surface or canvas-subdivision.
      canvas: canvas,
      backgroundAlpha: 0,
      width: dimensions.x,
      height: dimensions.y,
      preference: "webgl", // TODO: Use webgpu for more performance
      webgpu: {
        antialias: true,
        preserveDrawingBuffer: true,
      },
      webgl: {
        antialias: true,
        preserveDrawingBuffer: true,
      },
    } as any as PIXI.AutoDetectOptions;
    const renderer = await PIXI.autoDetectRenderer(opts);
    CanvasState.renderers.set(canvasType, renderer);
    return renderer;
  }

  getCanvasFromType(canvasType?: CanvasType) {
    if (canvasType) {
      return CanvasState.canvass.get(canvasType);
    }
    return CanvasState.canvass.values().next().value;
  }

  getRendererFromCanvasType(canvasType?: CanvasType): PIXI.Renderer {
    if (canvasType) {
      return CanvasState.renderers.get(canvasType);
    }
    return CanvasState.renderers.values().next().value;
  }

  getCanvasDimensions(canvasType: CanvasType = CanvasType.PostsCanvas): Vector2 {
      return CanvasState.canvasDimensions.get(canvasType)
  }


  getCanvasTexture(canvasType?: CanvasType) {
    if (canvasType) {
      return CanvasState.canvasTextures.get(canvasType);
    }
    return CanvasState.canvasTextures.values().next().value
  }

  getCanvasStage(canvasType?: CanvasType): PIXI.Container {
    if (canvasType) {
      return CanvasState.canvasStages.get(canvasType);
    }
    return CanvasState.canvasStages.values().next().value
  }

  async loadPixiAssets() {
    PIXI.Assets.addBundle("fonts", [
      {
        alias: "fa-regular",
        src: "/assets/fonts/font-awesome/fa-regular-400.woff2",
        data: {
          family: "fa-regular",
          format: "woff2",
        },
      },
      {
        alias: "fa-solid",
        src: "/assets/fonts/font-awesome/fa-solid-900.woff2",
        data: {
          family: "fa-solid",
          format: "woff2",
        },
      },
      { alias: "helvetica", src: "/assets/fonts/helvetica-255/Helvetica.ttf" },
      {
        alias: "Lineal-test",
        src: "https://pixijs.com/assets/webfont-loader/Lineal.otf",
      },
      {
        alias: "hrb",
        src: "/assets/fonts/helvetica-255/helvetica-rounded-bold.otf",
      },
      {
        alias: "inter",
        src: "/assets/fonts/inter/Inter_18pt-Regular.ttf",
        data: {
          family: "inter",
        },
      },
    ]);
    await PIXI.Assets.loadBundle("fonts");
  }


  async render() {
    for (const canvasType of Array.from(
        CanvasState.canvass.keys(),
    )) {
      const renderer = CanvasState.renderers.get(canvasType);
      const stage = CanvasState.canvasStages.get(canvasType);
      const canvasTexture = CanvasState.canvasTextures.get(canvasType);
      await renderer.render(stage);
      canvasTexture.needsUpdate = true;
    }
  }
}
